import type { ProfileModuleLike } from './types';

const POLICY_KEY = 'policy';

type EntryWithPolicy = Record<string, unknown> & { policy?: string };

export type StripTemplatePolicyOptions = {
  /** Profiles do not persist team member roster copied from templates. */
  clearTeamMembers?: boolean;
};

export function stripPolicyFromEntry<T extends EntryWithPolicy>(entry: T): T {
  const rest = { ...entry };
  delete rest.policy;
  return rest;
}

export function stripConfigPolicy(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const rest = { ...config };
  delete rest[POLICY_KEY];
  return rest;
}

/** Removes template-only `policy` fields before persisting profile module content. */
export function stripTemplatePolicyFromModule<T extends ProfileModuleLike>(
  module: T,
  options?: StripTemplatePolicyOptions,
): T {
  const config = stripConfigPolicy({ ...(module.config ?? {}) });
  const type = module.type;

  switch (type) {
    case 'socials':
      config.links = (
        (config.links as EntryWithPolicy[] | undefined) ?? []
      ).map((link) => stripPolicyFromEntry(link));
      break;
    case 'contact_info':
      config.items = (
        (config.items as EntryWithPolicy[] | undefined) ?? []
      ).map((item) => stripPolicyFromEntry(item));
      break;
    case 'links':
      config.items = (
        (config.items as EntryWithPolicy[] | undefined) ?? []
      ).map((item) => stripPolicyFromEntry(item));
      break;
    case 'images':
      config.images = (
        (config.images as EntryWithPolicy[] | undefined) ?? []
      ).map((img) => stripPolicyFromEntry(img));
      break;
    case 'files':
      config.files = (
        (config.files as EntryWithPolicy[] | undefined) ?? []
      ).map((file) => stripPolicyFromEntry(file));
      break;
    case 'team_members':
      config.members = options?.clearTeamMembers
        ? []
        : ((config.members as EntryWithPolicy[] | undefined) ?? []).map(
            (member) => stripPolicyFromEntry(member),
          );
      break;
    default:
      break;
  }

  return {
    ...module,
    config,
  };
}

export function stripTemplatePolicyFromModules<T extends ProfileModuleLike>(
  modules: T[],
  options?: StripTemplatePolicyOptions,
): T[] {
  return modules.map((module) =>
    stripTemplatePolicyFromModule(module, options),
  );
}
